import axios, {AxiosRequestConfig} from "axios";
import {set} from "lodash";
import {API_URL, JSON_URL} from "modules/constants";
import {IApi} from "modules/utils/Api/api";
import {CANCEL} from "redux-saga";
import {HttpClientService} from "modules/utils/HTTP";

class HTTPClient extends HttpClientService {
	/**
	 * Overridden method adds CancelToken symbol, that allow redux-saga'
	 * "takeLatest" function to cancel any requests automatically.
	 * http://fe-common-utils.s3-website-eu-west-1.amazonaws.com/classes/httpclient.html
	 */
	public request<T>(config: AxiosRequestConfig): Promise<T> {
		const source = axios.CancelToken.source();

		const request = super.request<T>({
			...config,
			cancelToken: source.token,
		});

		return set<Promise<T>>(request, CANCEL, () => source.cancel());
	}
}

// type ApiResponseError = {
// 	errors: {
// 		message: string;
// 	}[];
// 	success: Record<string, unknown>;
// };

// const onCatchNetworkError = ({
// 	response,
// 	message = "Network error",
// }: AxiosError<ApiResponseError>) => {
// 	const data = response?.data;
// 	const error = isObject(data)
// 		? {
// 				...data,
// 				errors: data?.errors?.map((it) => ({
// 					message: it.message,
// 					code: response?.status,
// 				})),
// 		  }
// 		: {
// 				errors: [new ApiError(message, response?.status)],
// 		  };
//
// 	return Promise.reject(error).catch((err: unknown) => ApiError.CHECK(err as IApiResponse));
// };

function withTimeStamp(url: string, separator = "?") {
	const timestamp = new Date().getTime();
	return `${url}${separator}_=${timestamp}`;
}

export const APIClient = new HTTPClient({
	baseURL: API_URL,
	withCredentials: true,
	// onCatchNetworkError,
});

export const JSONClient = new HTTPClient({
	baseURL: JSON_URL,
});

export const Api: IApi = {
	Jsons: {
		players: () => JSONClient.get(`/players.json`),
		suggested_players: () => JSONClient.get(`/suggested_players.json`),
		squads: () => JSONClient.get(`/squads.json`),
		settings: () => JSONClient.get(`/settings.json`),
		rounds: () => JSONClient.get(`/rounds.json`),
		units: () => JSONClient.get(`/units.json`),
		history_units: () => JSONClient.get(`/units_history.json`),
		checksums: () => JSONClient.get(`/checksums.json`),
		terms: () => JSONClient.get(`/terms.json`),
		prizes: () => JSONClient.get(`/prizes.json`),
		tutorial: () => JSONClient.get(`/tutorial.json`),
		mulligan: () => JSONClient.get(`/mulligan.json`),
		game_message: () => JSONClient.get(`/game_message.json`),
		reward_stats: () => JSONClient.get(`/reward_stats.json`),
	},
	Auth: {
		backdoor: (params) =>
			APIClient.post(`/auth/backdoor`, params, {
				headers: {
					"content-type": "multipart/form-data",
				},
			}),
		login: (params) => APIClient.post("/auth/login", params),
		logout: () => APIClient.post("/auth/logout"),
	},
	User: {
		session: () => APIClient.get("/user"),
		enable_mulligan: () => APIClient.post("/user/enable_mulligan"),
		view_tutorial: () => APIClient.get("/user/view_tutorial"),
		view_banner: (params) => APIClient.post("/user/view_banner", params),
		view_second_tutorial: () => APIClient.get("/user/view_second_tutorial"),
		view_game_message: () => APIClient.get("/user/view_game_message"),
		view_tooltip: () => APIClient.get("/user/view_tooltip"),
		view_bubble_message: () => APIClient.get("/user/view_bubble_message"),
		check_terms: (uid) => APIClient.get(withTimeStamp(`/user/check_terms/${String(uid)}`)),
		check_username: (uid) =>
			APIClient.get(withTimeStamp(`/user/check_username/${String(uid)}`)),
		check_country: () => APIClient.get(`/geo/check_country`),
		country: () => APIClient.get(`/geo/country`),
		favorite_squads: () => APIClient.get(`/user/favorite_squads`),
		favorite_players: () => APIClient.get(`/user/favorite_players`),
		accept_terms: () => APIClient.post("/user/accept_terms"),
		view_reward_modal: () => APIClient.post("/user/view_reward_modal"),
	},
	Prediction: {
		save: (params) => APIClient.post(`/predictions`, params),
		get: () => APIClient.get(`/predictions`),
		history: (params) => APIClient.get("/prediction/history", params),
		friends_history: (params) => APIClient.get("/prediction/friends_history", params),
		leaders: (params) => APIClient.get("/prediction/leaders", params),
		friends_selected_player: ({unitId, playerId}) =>
			APIClient.get(
				`/prediction/${String(unitId)}/friends_selected_player/${String(playerId)}`
			),
	},
	Results: {
		dashboard: () => APIClient.get("results/dashboard"),
	},
	Player: {
		detail: (playerId, unitId, pitcherId) =>
			APIClient.get(
				`/players/${String(playerId)}/batter_details/${String(unitId)}${
					pitcherId ? `/${String(pitcherId)}` : ""
				}`
			),
	},
	Rank: {
		leaderboard: ({leagueId, roundId, ...params}) => {
			let url = "rank/leaderboard";

			if (leagueId) {
				url += `/league/${leagueId}`;
			}
			if (roundId) {
				url += `/round/${roundId}`;
			}

			return APIClient.get(url, params);
		},
		profile: ({userId, ...params}) => APIClient.get(`rank/user/${userId}/profile`, params),
	},
	Leagues: {
		create: (params) => APIClient.post("league/create", params),
		update: ({leagueId, ...params}) => APIClient.post(`league/${leagueId}`, params),
		my_leagues: (params) => APIClient.get("league/joined", params),
		leagues_to_join: (params) => APIClient.get("league/all", params),
		join_league: ({leagueCode}) => APIClient.post(`league/${leagueCode}/join`),
		league_by_id: ({id}) => APIClient.get(`league/${id}`),
		delete: ({leagueId}) => APIClient.post(`league/${leagueId}/delete`),
		leave: ({leagueId}) => APIClient.post(`league/${leagueId}/leave`),
		league_users: ({leagueId, ...params}) => APIClient.get(`league/${leagueId}/users`, params),
		remove_user: ({leagueId, userId}) =>
			APIClient.post(`league/${leagueId}/delete-user/${userId}`),
	},
	Reward: {
		show_my: () => APIClient.get("rewards/show-my"),
	},
};

export * from "./ApiError";

export default Api;
